import { useRef, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toolName } from '@dale/plans';

import { getOnboarding } from '../api/endpoints/onboarding';
import type { BusinessLocation } from '../api/schemas';
import { insideNativeApp } from '../auth/native-app';
import { isCoach, isOwner } from '../auth/roles';
import { usePermissions } from '../auth/usePermissions';
import { useSession } from '../auth/useSession';
import { modeForCategory } from '../lib/mode';
import { planLabel, type PlanTier } from '../lib/plan';
import { useBusiness } from '../lib/useBusiness';
import { useFeatures } from '../lib/useFeatures';
import { useTools } from '../lib/useTools';
import { whoAmI } from '../lib/whoami';
import { AgentsNavLink } from '../features/agents/AgentsNavLink';
import { IntelligenceLauncher } from '../features/intelligence/IntelligenceLauncher';
import { IntelligenceNavLink } from '../features/intelligence/IntelligenceNavLink';
import { AccountMenu } from './AccountMenu';
import { filterByPermission, withPlanAccess } from './shell/navAccess';
import { NavLabel } from './shell/NavLabel';
import { NavFold } from './NavFold';
import { TabIcon } from './shell/TabIcon';
import { useLocations } from './shell/useLocations';
import { useActiveIndicator } from '../lib/motion/hooks';
import { Sheet } from './Sheet';

/** A coach's whole shell: their classes and their bookings in a studio, their agenda anywhere else. */
function coachLinks(portalMode: string): { to: string; label: string; exact: boolean }[] {
  return portalMode === 'classes'
    ? [
        { to: '/clases', label: 'Mis clases', exact: false },
        { to: '/reservas', label: 'Reservas', exact: false },
      ]
    : [
        { to: '/', label: 'Mi agenda', exact: true },
        { to: '/calendario', label: 'Mi calendario', exact: false },
      ];
}

/** Service businesses read their day by chair; desktop sidebar and the phone "Más" sheet. */
const CALENDAR_LINK = { to: '/calendario', label: 'Calendario', exact: false } as const;
const CLASSES_LINK = { to: '/clases', label: 'Clases', exact: false } as const;

const ONBOARDING_QUERY_KEY = ['business-onboarding'];

const SHARED_LINKS = [
  { to: '/', label: 'Inicio', exact: true },
  { to: '/cobrar', label: 'Cobrar', exact: false },
  { to: '/pagos', label: 'Pagos', exact: false },
  { to: '/clientes', label: 'Clientes', exact: false },
  { to: '/historial', label: 'Historial', exact: false },
] as const;

const COACH_COUNTER_LINKS = SHARED_LINKS.filter((link) => link.to !== '/' && link.to !== '/clientes');

/** Owners open on the summary, so their agenda needs its own door. The team's
    Inicio IS the agenda, so they never get a second entry. */
const OWNER_OPERATION_LINKS = [{ to: '/reservas', label: 'Reservas', exact: false }] as const;

/** Max and up: Dale Agents' reports sit beside the agents instead of in Administración. */
const INTELLIGENCE_LINK = { to: '/inteligencia', label: 'Informes', exact: false } as const;

/** Dale Agents and the inbox of what Luna talks over with customers; both lead the owner's nav. */
const AGENTS_PATH = '/agentes';
const INBOX_LINK = { to: '/bandeja', label: 'Bandeja', exact: false } as const;

const OWNER_LINKS = [
  { to: '/servicios', label: 'Servicios' },
  { to: '/agenda', label: 'Agenda' },
  { to: '/equipo', label: 'Equipo' },
  { to: '/ofertas', label: 'Ofertas' },
  { to: '/negocio', label: 'Negocio' },
  { to: '/finanzas', label: 'Finanzas' },
  { to: '/facturas', label: 'Facturas' },
] as const;

type NavLinkProps = {
  to: string;
  label: string;
  exact?: boolean;
  /** The plan that opens this page, when the business's plan does not include it. */
  lock?: PlanTier;
  className: string;
  onNavigate?: () => void;
  /** Tab bar only: draws the route's glyph above the label. */
  withIcon?: boolean;
};

function NavLink({ to, label, exact, lock, className, onNavigate, withIcon }: NavLinkProps) {
  return (
    <Link
      to={to}
      className={className}
      activeOptions={{ exact: exact ?? false }}
      activeProps={{ 'data-status': 'active' }}
      onClick={onNavigate}
    >
      {withIcon ? <TabIcon name={to} /> : null}
      <NavLabel label={label} lock={lock} />
    </Link>
  );
}

type OperationLink = { to: string; label: string; exact: boolean; lock?: PlanTier };

/** Inicio, then the studio's class places folded under "Clases", then the counter work. */
function StudioOperationLinks({ links, owner, calendar }: { links: OperationLink[]; owner: boolean; calendar: boolean }) {
  const classes = links.find((link) => link.to === CLASSES_LINK.to);
  const folded: OperationLink[] = [
    ...(classes ? [{ ...classes, label: 'Horario' }] : []),
    ...(calendar ? [CALENDAR_LINK] : []),
    ...(owner ? OWNER_OPERATION_LINKS : []),
  ];
  const [home, ...counter] = links.filter((link) => link.to !== CLASSES_LINK.to);
  return (
    <>
      {home ? <NavLink {...home} className="nav-item" /> : null}
      <NavFold foldId="clases" label="Clases" paths={folded.map((link) => link.to)}>
        {folded.map((link) => (
          <NavLink key={link.to} {...link} className="nav-item" />
        ))}
      </NavFold>
      {counter.map((link) => (
        <NavLink key={link.to} {...link} className="nav-item" />
      ))}
    </>
  );
}

type LocationSwitcherProps = {
  locations: BusinessLocation[];
  onSwitch: (businessId: string) => void;
  pending: boolean;
};

/** Only shows once there is a real choice: a single location has nothing to switch to. */
export function LocationSwitcher({ locations, onSwitch, pending }: LocationSwitcherProps) {
  if (locations.length <= 1) return null;
  const activeId = locations.find((location) => location.active)?.id ?? locations[0]!.id;
  return (
    <select
      aria-label="Local"
      value={activeId}
      disabled={pending}
      onChange={(event) => onSwitch(event.target.value)}
    >
      {locations.map((location) => (
        <option key={location.id} value={location.id}>
          {location.name}
        </option>
      ))}
    </select>
  );
}

function Brand({ compact }: { compact?: boolean }) {
  const word = (
    <span className="brand-word">
      Dale<span className="dot">.</span>
    </span>
  );
  if (compact) return word;
  return (
    <span>
      {word}
      <span className="brand-sub">Negocios</span>
    </span>
  );
}

/**
 * The whole shell chrome: desktop sidebar, phone top bar + tab bar, and the
 * phone "Más" sheet. CSS shows exactly one navigation per viewport.
 */
export function NavBar() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const places = useRef<HTMLDivElement>(null);
  const tabs = useRef<HTMLElement>(null);
  useActiveIndicator(places, ".nav-item[data-status='active']", pathname);
  useActiveIndicator(tabs, ".tab[data-status='active']", pathname);
  const { session, logout } = useSession();
  const owner = isOwner(session?.role);
  const coach = isCoach(session);
  const { can, profile } = usePermissions();
  const business = useBusiness();
  const { features } = useFeatures();
  const { tools, category } = useTools();
  const [sheetOpen, setSheetOpen] = useState(false);

  const { locations, switchMutation } = useLocations(owner);
  const onboardingQuery = useQuery({
    queryKey: ONBOARDING_QUERY_KEY,
    queryFn: getOnboarding,
    enabled: owner,
  });

  // A coach reads their own name and role first; everyone else reads the business.
  const who = whoAmI(session, business, profile);
  const businessName = who.name;
  const roleWord = who.roleWord;
  const closeSheet = () => setSheetOpen(false);
  // The floor editor exists for restaurants (Mesas) and class studios
  // (Espacios); service businesses never see it.
  const portalMode = business ? modeForCategory(business.category) : 'services';
  const ownerLinks = [
    ...(onboardingQuery.data?.state === 'draft'
      ? [{ to: '/primer-dia', label: 'Primer día' }]
      : []),
    // A restaurant's Menú is the catalog's `menu` (Pro): below it the door stays, locked.
    ...OWNER_LINKS.map((link) =>
      link.to === '/servicios' && portalMode === 'restaurant'
        ? { ...link, label: 'Menú', capability: 'menu' as const }
        : link,
    ),
  ];
  // The roster and the comandas are front-desk work, so the whole team gets
  // them, not just owners.
  const seesAgenda = can('agenda');
  // Every type gets the Calendario (restaurants too, since 2026-10-28), unless the owner turned it off.
  const showsCalendar = !coach && portalMode !== 'classes' && tools.calendario && seesAgenda;
  const studioCalendar = tools.calendario && seesAgenda;
  // A type's group sessions (Talleres, Grupos) sit with the operation when they are on; a studio folds its own.
  const sessionsLink = { ...CLASSES_LINK, label: category ? toolName('clases', category) : CLASSES_LINK.label };
  // Ultra's agents lead the nav for the owner: they are who prepares the work. The same plan and
  // feature rules as the Helios nav decide each door, so no look shows less than another.
  // Unknown while the business loads; a loaded business with no plan reads as Base, like the server.
  const plan = business ? (business.plan ?? 'base') : undefined;
  // Every plan shows the same doors: `door` is undefined only for one switched off, or while the plan is unknown.
  const door = (path: string) => (owner ? withPlanAccess([{ to: path }], plan, features)[0] : undefined);
  const agentsDoor = door(AGENTS_PATH);
  const inboxDoor = door(INBOX_LINK.to);
  const intelligenceDoor = door(INTELLIGENCE_LINK.to);
  const hasAgents = Boolean(agentsDoor);
  const hasInbox = Boolean(inboxDoor);
  const hasIntelligence = Boolean(intelligenceDoor);
  // A studio's classes are its day, so they sit right under Inicio, folded
  // together with the calendar and the bookings (the phone gets them as its second tab).
  const studioFold = !coach && portalMode === 'classes';
  // A coach keeps their own shell and gains the counter doors their profile has
  // on; they meet their clients in the class roster, so the list stays out.
  const operationLinks = coach
    ? [
        ...coachLinks(portalMode),
        ...filterByPermission(
          withPlanAccess(COACH_COUNTER_LINKS, plan, features),
          can,
          owner,
        ),
      ]
    : filterByPermission(
        withPlanAccess<{ to: string; label: string; exact: boolean }>(
          portalMode === 'classes'
            ? [SHARED_LINKS[0], ...(tools.clases ? [CLASSES_LINK] : []), ...SHARED_LINKS.slice(1)]
            : [
                ...SHARED_LINKS,
                ...(portalMode === 'restaurant' ? [{ to: '/comandas', label: 'Comandas', exact: false } as const] : []),
                ...(tools.clases ? [sessionsLink] : []),
              ],
          plan,
          features,
        ),
        can,
        owner,
      );
  // Every type gets its spaces and its plans or packages, named in its trade's words, when they are on.
  const adminLinks = filterByPermission(withPlanAccess(
    [
      ...(tools.espacios ? [{ to: '/mesas', label: category ? toolName('espacios', category) : 'Espacios' }] : []),
      ...(tools.planes ? [{ to: '/planes', label: category ? toolName('planes', category) : 'Planes' }] : []),
      ...ownerLinks,
      ...(owner ? [{ to: '/herramientas', label: 'Herramientas' }] : []),
    ],
    plan,
    features,
  ), can, owner);

  return (
    <>
      <nav className="sidebar" aria-label="Principal">
        <Link to="/" className="brand" aria-label="Dale Negocios, inicio">
          <Brand />
        </Link>
        {/* Only the places scroll; the brand stays on top and the account stays pinned at the foot. */}
        <div className="sidebar-scroll" ref={places}>
        <span className="ind nav-thumb" aria-hidden="true" />
        {hasAgents || hasIntelligence || hasInbox ? (
          <div className="nav-group">
            {hasAgents ? <AgentsNavLink lock={agentsDoor?.lock} /> : null}
            {hasIntelligence ? <IntelligenceNavLink lock={intelligenceDoor?.lock} /> : null}
            {hasInbox ? <NavLink {...INBOX_LINK} lock={inboxDoor?.lock} className="nav-item" /> : null}
          </div>
        ) : null}
        <div className="nav-group">
          <p className="nav-group-label">Operación</p>
          {studioFold ? (
            <StudioOperationLinks links={operationLinks} owner={owner} calendar={studioCalendar} />
          ) : (
            <>
              {operationLinks.map((link) => (
                <NavLink key={link.to} {...link} className="nav-item" />
              ))}
              {owner
                ? OWNER_OPERATION_LINKS.map((link) => (
                    <NavLink key={link.to} {...link} className="nav-item" />
                  ))
                : null}
              {showsCalendar ? <NavLink {...CALENDAR_LINK} className="nav-item" /> : null}
            </>
          )}
        </div>
        {adminLinks.length > 0 ? (
          <div className="nav-group">
            <p className="nav-group-label">Administración</p>
            {adminLinks.map((link) => (
              <NavLink key={link.to} {...link} className="nav-item" />
            ))}
          </div>
        ) : null}
        </div>
        <div className="sidebar-foot">
          {owner ? (
            <LocationSwitcher
              locations={locations}
              onSwitch={(businessId) => switchMutation.mutate(businessId)}
              pending={switchMutation.isPending}
            />
          ) : null}
          <AccountMenu
            name={businessName}
            roleWord={roleWord}
            initials={who.initials}
            {...(owner && business && !insideNativeApp() ? { planLabel: planLabel(business.plan ?? 'base') } : {})}
            onLogout={() => void logout()}
          />
        </div>
      </nav>

      <header className="topbar">
        <Brand compact />
        <span className="who">{businessName}</span>
        <IntelligenceLauncher />
      </header>

      <nav className="tabbar" aria-label="Pestañas" ref={tabs}>
        <span className="ind tab-thumb" aria-hidden="true" />
        {operationLinks.map((link) => (
          <NavLink key={link.to} {...link} className="tab" withIcon />
        ))}
        <button
          type="button"
          className="tab"
          aria-expanded={sheetOpen}
          onClick={() => setSheetOpen(true)}
        >
          <TabIcon name="more" />
          Más
        </button>
      </nav>

      <Sheet open={sheetOpen} label="Más opciones" onClose={closeSheet}>
        {hasAgents ? <AgentsNavLink lock={agentsDoor?.lock} onNavigate={closeSheet} /> : null}
        {hasIntelligence ? <IntelligenceNavLink lock={intelligenceDoor?.lock} onNavigate={closeSheet} /> : null}
        {hasInbox ? <NavLink {...INBOX_LINK} lock={inboxDoor?.lock} className="nav-item" onNavigate={closeSheet} /> : null}
        {studioFold ? <p className="nav-group-label">Clases</p> : null}
        {showsCalendar || (studioFold && studioCalendar) ? <NavLink {...CALENDAR_LINK} className="nav-item" onNavigate={closeSheet} /> : null}
        {owner
          ? OWNER_OPERATION_LINKS.map((link) => (
              <NavLink key={link.to} {...link} className="nav-item" onNavigate={closeSheet} />
            ))
          : null}
        {adminLinks.length > 0 && studioFold ? <p className="nav-group-label">Administración</p> : null}
        {adminLinks.map((link) => (
          <NavLink key={link.to} {...link} className="nav-item" onNavigate={closeSheet} />
        ))}
        {owner ? (
          <LocationSwitcher
            locations={locations}
            onSwitch={(businessId) => switchMutation.mutate(businessId)}
            pending={switchMutation.isPending}
          />
        ) : null}
        <p className="nav-group-label">Tu cuenta</p>
        {owner && business && !insideNativeApp() ? (
          <Link to="/membresia" className="nav-item" onClick={closeSheet}>
            Tu plan <span className="account-menu-hint">{planLabel(business.plan ?? 'base')}</span>
          </Link>
        ) : null}
        <NavLink to="/configuracion" label="Configuración" className="nav-item" onNavigate={closeSheet} />
        <button type="button" className="nav-item sheet-logout" onClick={() => void logout()}>
          Salir
        </button>
      </Sheet>
    </>
  );
}
